<script setup lang="ts">
import { Label } from '@/components/ui/label';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import { resolveProjectMilestones } from '~/lib/projectMilestones';
import type { ProjectStage } from '~/types/models';

type ProjectWithMilestones = {
    id: string;
    name: string;
    stages?: ProjectStage[];
};

const props = withDefaults(defineProps<{
    modelValue?: string;
    projectId?: string;
    projectOptions?: ProjectWithMilestones[];
    milestones?: ProjectStage[];
    label?: string;
    labelClass?: string;
    description?: string;
    placeholder?: string;
    clearLabel?: string;
    searchPlaceholder?: string;
    storageKey?: string;
    variant?: 'form' | 'default';
    triggerClass?: string;
    hideWhenEmpty?: boolean;
}>(), {
    modelValue: '',
    projectId: '',
    projectOptions: () => [],
    milestones: undefined,
    label: 'Milestone',
    labelClass: '',
    description: '',
    placeholder: 'No milestone',
    clearLabel: 'No milestone',
    searchPlaceholder: 'Search milestones...',
    storageKey: 'task-milestone-picker',
    variant: 'form',
    triggerClass: '',
    hideWhenEmpty: true,
});

const emit = defineEmits<{
    (event: 'update:model-value', value: string): void;
}>();

const selectedProject = computed(() => (
    props.projectOptions.find(project => project.id === props.projectId) || null
));
const resolvedMilestones = computed(() => {
    if (props.milestones !== undefined) {
        return resolveProjectMilestones(props.milestones);
    }
    if (selectedProject.value) {
        return resolveProjectMilestones(selectedProject.value.stages);
    }
    return [];
});
const options = computed(() => resolvedMilestones.value.map(milestone => ({
    value: milestone.id,
    label: milestone.name,
    description: milestone.description,
})));
const isVisible = computed(() => !props.hideWhenEmpty || options.value.length > 0);

watch(
    () => [props.projectId, props.modelValue, options.value.map(option => option.value).join('|')],
    () => {
        const current = String(props.modelValue || '').trim();
        if (!current || options.value.length === 0) return;
        if (!options.value.some(option => option.value === current)) {
            emit('update:model-value', '');
        }
    },
);
</script>

<template>
    <div v-if="isVisible" class="min-w-0 space-y-2">
        <Label v-if="label" :class="labelClass">{{ label }}</Label>
        <SearchableSelectMenu
            :model-value="String(modelValue || '')"
            :options="options"
            :placeholder="placeholder"
            :search-placeholder="searchPlaceholder"
            allow-clear
            :clear-label="clearLabel"
            show-all-by-default
            :storage-key="storageKey"
            :variant="variant"
            :trigger-class="triggerClass"
            @update:model-value="emit('update:model-value', String($event || ''))"
        />
        <p v-if="description" class="text-xs text-muted-foreground">{{ description }}</p>
    </div>
</template>
